import {
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  Folder as FolderIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";
import { FileItem as FileItemType } from "../../types/interfaces/filesStorage";

export type TimeDisplayMode = "utc" | "local";

interface FileItemProps {
  item: FileItemType;
  onItemClick?: (item: FileItemType) => void;
  onDownloadClick?: (item: FileItemType) => void;
  onMoreOptionsClick?: (item: FileItemType, event: React.MouseEvent) => void;
  showDownloadButton?: boolean;
  showMoreOptions?: boolean;
  disableMoreOptions?: boolean;
  moreOptionsDisabledTooltip?: string;
  disabled?: boolean;
  isDownloading?: boolean;
  downloadProgress?: number;
  backgroundColor?: string;
  timeDisplayMode?: TimeDisplayMode;
  showFileDate?: boolean;
  hideEmptyLabel?: boolean;
  moreOptionsFoldersOnly?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

const FileItem: React.FC<FileItemProps> = ({
  item,
  onItemClick,
  onDownloadClick,
  onMoreOptionsClick,
  showDownloadButton = true,
  showMoreOptions = false,
  disableMoreOptions = false,
  moreOptionsDisabledTooltip = "",
  disabled = false,
  isDownloading = false,
  downloadProgress = 0,
  backgroundColor,
  timeDisplayMode = "local",
  showFileDate = true,
  hideEmptyLabel = false,
  moreOptionsFoldersOnly = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isFolder = item.ftype === "folder";

  const handleItemClick = () => {
    if (!disabled && onItemClick) {
      onItemClick(item);
    }
  };

  const handleDownloadClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (disabled || isDownloading) return;

    if (onDownloadClick) {
      onDownloadClick(item);
    }
  };

  const handleMoreOptionsClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (disabled || disableMoreOptions || !onMoreOptionsClick) return;
    onMoreOptionsClick(item, event);
  };



  const renderIcon = () => {
    return (
      <ListItemIcon>
        {isFolder ? (
          <FolderIcon sx={{ color: "primary.main" }} />
        ) : (
          <FileIcon sx={{ color: "primary.main" }} />
        )}
      </ListItemIcon>
    );
  };

  const formatDateTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const tz = timeDisplayMode === "utc" ? "UTC" : undefined;
    if (isMobile) {
      return d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: tz,
      });
    }
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZone: tz,
    });
  };

  const renderText = () => {
    const folders = item.subfolder_count ?? 0;
    const files = item.direct_file_count ?? 0;
    const hasCounts = isFolder && (folders > 0 || files > 0);
    const isEmpty = isFolder && folders === 0 && files === 0;
    let countLabel: string | null = null;
    if (hasCounts) {
      if (folders > 0 && files > 0) {
        countLabel = `${folders} folder${folders !== 1 ? "s" : ""}, ${files} file${files !== 1 ? "s" : ""}`;
      } else if (folders > 0) {
        countLabel = `${folders} folder${folders !== 1 ? "s" : ""}`;
      } else {
        countLabel = `${files} file${files !== 1 ? "s" : ""}`;
      }
    } else if (isEmpty && !hideEmptyLabel) {
      countLabel = "empty";
    }
    const metadataSx = {
      whiteSpace: "nowrap" as const,
      ml: { xs: 1, sm: 2 },
      fontSize: { xs: "0.7rem", sm: "0.75rem" },
      flexShrink: 0,
      pr: { xs: 1, sm: 1.5 },
    };
    const uploading = item.is_uploading;
    const creating = item.is_creating;
    const pending = uploading || creating;
    return (
      <>
        <ListItemText
          primary={item.fname}
          primaryTypographyProps={{
            sx: { flex: 1, minWidth: 0 },
          }}
        />
        {pending && (
          <Typography variant="caption" color="text.secondary" sx={metadataSx}>
            {uploading ? "Uploading…" : "Creating…"}
          </Typography>
        )}
        {!pending && showFileDate && !isFolder && item.last_modified && (
          <Typography variant="caption" color="text.secondary" sx={metadataSx}>
            {formatDateTime(item.last_modified)}
          </Typography>
        )}
        {!pending && !isFolder && item.file_size != null && (
          <Typography variant="caption" color="text.secondary" sx={metadataSx}>
            {formatFileSize(item.file_size)}
          </Typography>
        )}
        {!pending && isFolder && countLabel && (
          <Typography variant="caption" color="text.secondary" sx={metadataSx}>
            {countLabel}
          </Typography>
        )}
      </>
    );
  };

  const renderActions = () => {
    const directFiles = item.direct_file_count ?? 0;
    const uploading = item.is_uploading;
    const creating = item.is_creating;
    const pending = uploading || creating;
    const shouldShowDownload =
      showDownloadButton &&
      !pending &&
      (isFolder ? directFiles > 0 : true);
    const folderOkForMore =
      !moreOptionsFoldersOnly || item.ftype === "folder";
    const shouldShowMoreOptions =
      showMoreOptions && !!onMoreOptionsClick && !pending && folderOkForMore;

    if (!shouldShowDownload && !shouldShowMoreOptions && !pending) {
      return null;
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {pending && showMoreOptions && (
          <IconButton disabled sx={{ cursor: "default" }}>
            <CircularProgress size={24} />
          </IconButton>
        )}
        {pending && !showMoreOptions && (
          <CircularProgress
            size={16}
            sx={{ ml: { xs: 0.5, sm: 1 } }}
          />
        )}
        {shouldShowDownload && (
          <IconButton
            onClick={handleDownloadClick}
            disabled={disabled || isDownloading}
            className="text-gray-500 hover:text-gray-700"
          >
            {isDownloading ? (
              <CircularProgress
                size={16}
                variant="determinate"
                value={downloadProgress}
                sx={{
                  color:
                    downloadProgress === 100
                      ? "success.main"
                      : "primary.main",
                  "& .MuiCircularProgress-circle": {
                    strokeLinecap: "round",
                  },
                }}
              />
            ) : (
              <DownloadIcon />
            )}
          </IconButton>
        )}
        {shouldShowMoreOptions &&
          (disableMoreOptions && moreOptionsDisabledTooltip ? (
            <Tooltip title={moreOptionsDisabledTooltip}>
              <span>
                <IconButton
                  onClick={handleMoreOptionsClick}
                  disabled={true}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <MoreVertIcon />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <IconButton
              onClick={handleMoreOptionsClick}
              disabled={disabled || disableMoreOptions}
              className="text-gray-500 hover:text-gray-700"
            >
              <MoreVertIcon />
            </IconButton>
          ))}
      </Box>
    );
  };

  return (
    <ListItem
      button
      onClick={handleItemClick}
      disableGutters
      className="w-full"
      style={{
        padding: "4px 16px",
        backgroundColor: backgroundColor || "transparent"
      }}
      sx={{
        alignItems: "center",
        "& .MuiListItemText-root": { minWidth: 0, flex: "1 1 auto" },
        "& .MuiListItemText-primary": {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
      }}
    >
      {renderIcon()}
      {renderText()}
      {renderActions()}
    </ListItem>
  );
};

export default FileItem;
